import { supabase } from "@/integrations/supabase/client";
import type { TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import type { Quizrun, Event, Location } from "@/types/domain";
import { logger, serializeError } from "@/lib/logger";
import { locationOrVirtual } from "@/lib/virtual";

// Public view columns. part1_form/part2_form are NOT in v_quizrun (host/admin-only,
// play-form links) — they are merged in from the base table for admin/host readers.
interface QuizrunRow {
  id: number;
  number: number | null;
  kind: "standard" | "special";
  title: string;
  language: string;
  date_start: string;
  date_finish: string;
  photo_link: string | null;
  part1_form?: string | null;
  part2_form?: string | null;
  is_finished: boolean;
  google_results_view?: string | null;
  answer_bonus_seconds?: number | null;
}

interface EventRow {
  id: number;
  quizrun_id: number;
  /** Null for a virtual event. */
  location_id: number | null;
  custom_content?: string | null;
  date_start: string;
  date_finish: string;
  gun_time: string | null;
  price_cents: number | null;
  price_currency?: string | null;
  location: {
    id: number | null;
    location_name: string;
    city: string;
    country: string;
    location_link: string | null;
    route_link: string | null;
    timezone: string | null;
  };
}

const EVENT_COLUMNS =
  "id, quizrun_id, location_id, date_start, date_finish, gun_time, price_cents, price_currency, custom_content";

const transformLocation = (loc: EventRow["location"]): Location => ({
  id: loc.id,
  location_name: loc.location_name,
  city: loc.city,
  country: loc.country,
  location_link: loc.location_link || undefined,
  route_link: loc.route_link || undefined,
  timezone: loc.timezone || undefined,
});

const transformEvent = (row: EventRow): Event => ({
  id: row.id,
  quizrun_id: row.quizrun_id,
  location_id: row.location_id,
  location: transformLocation(row.location),
  custom_content: row.custom_content ?? undefined,
  date_start: row.date_start,
  date_finish: row.date_finish,
  gun_time: row.gun_time ?? undefined,
  price_cents: row.price_cents ?? undefined,
  price_currency: row.price_currency ?? undefined,
});

const transformQuizrunRow = (row: QuizrunRow, events: Event[]): Quizrun => ({
  id: row.id,
  number: row.number,
  kind: row.kind,
  title: row.title,
  language: row.language,
  date_start: row.date_start,
  date_finish: row.date_finish,
  events: events,
  // answer_link is admin-only, not exposed in public view
  photo_link: row.photo_link || undefined,
  part1_form: row.part1_form || undefined,
  part2_form: row.part2_form || undefined,
  is_finished: row.is_finished,
  google_results_view: row.google_results_view || undefined,
  answer_bonus_seconds: row.answer_bonus_seconds ?? undefined,
});

/**
 * Fetches all quizruns from the database with their events.
 */
export const fetchQuizruns = async (): Promise<Quizrun[]> => {
  // Fetch quizruns, events, and locations in parallel
  const [quizrunsResult, eventsResult, locationsResult] = await Promise.all([
    supabase
      .from("v_quizrun")
      .select("*")
      .order("date_start", { ascending: false }),
    supabase.from("v_event").select(EVENT_COLUMNS),
    supabase.from("v_location").select("*"),
  ]);

  if (quizrunsResult.error) {
    throw quizrunsResult.error;
  }
  if (eventsResult.error) {
    throw eventsResult.error;
  }
  if (locationsResult.error) {
    throw locationsResult.error;
  }

  const quizruns = quizrunsResult.data;
  const events = eventsResult.data;
  const locations = locationsResult.data;

  // Create location lookup map
  const locationMap = new Map(locations?.map((l) => [l.id, l]) || []);

  // Group events by quizrun_id and attach locations
  const eventsByQuizrun = new Map<number, Event[]>();
  for (const event of events || []) {
    const quizrunId = event.quizrun_id;
    const location = locationOrVirtual(locationMap, event.location_id);

    if (!eventsByQuizrun.has(quizrunId)) {
      eventsByQuizrun.set(quizrunId, []);
    }

    // Create EventRow-compatible object with location
    const eventWithLocation: EventRow = {
      ...event,
      location: location
        ? {
            id: location.id,
            location_name: location.location_name,
            city: location.city,
            country: location.country || "",
            location_link: location.location_link,
            route_link: location.route_link,
            timezone: location.timezone ?? null,
          }
        : {
            id: event.location_id,
            location_name: "Unknown",
            city: "Unknown",
            country: "",
            location_link: null,
            route_link: null,
            timezone: null,
          },
    };

    eventsByQuizrun.get(quizrunId)!.push(transformEvent(eventWithLocation));
  }

  return (quizruns as QuizrunRow[]).map((qr) =>
    transformQuizrunRow(qr, eventsByQuizrun.get(qr.id) || []),
  );
};

/**
 * Fetches a single quizrun by ID with its events.
 */
export const fetchQuizrun = async (
  quizrunId: number,
): Promise<Quizrun | null> => {
  const { data: quizrun, error: quizrunError } = await supabase
    .from("v_quizrun")
    .select("*")
    .eq("id", quizrunId)
    .maybeSingle();

  if (quizrunError) {
    throw quizrunError;
  }

  if (!quizrun) {
    return null;
  }

  // Fetch events and all locations in parallel (locations are few, no need to filter by ID)
  const [eventsResult, locationsResult] = await Promise.all([
    supabase.from("v_event").select(EVENT_COLUMNS).eq("quizrun_id", quizrunId),
    supabase.from("v_location").select("*"),
  ]);

  if (eventsResult.error) {
    throw eventsResult.error;
  }
  if (locationsResult.error) {
    throw locationsResult.error;
  }

  const events = eventsResult.data;
  const locations = locationsResult.data;
  const locationMap = new Map(locations?.map((l) => [l.id, l]) || []);

  const quizrunEvents = (events || []).map((event) => {
    const location = locationOrVirtual(locationMap, event.location_id);
    const eventWithLocation: EventRow = {
      ...event,
      location: location
        ? {
            id: location.id,
            location_name: location.location_name,
            city: location.city,
            country: location.country || "",
            location_link: location.location_link,
            route_link: location.route_link,
            timezone: location.timezone ?? null,
          }
        : {
            id: event.location_id,
            location_name: "Unknown",
            city: "Unknown",
            country: "",
            location_link: null,
            route_link: null,
            timezone: null,
          },
    };
    return transformEvent(eventWithLocation);
  });

  // part1_form/part2_form/google_results_view live on the base table (RLS: admin
  // full + host read), not the public view. Returns a row only for hosts/admins;
  // anon gets nothing.
  const { data: hostLinks, error: hostLinksError } = await supabase
    .from("quizrun")
    .select("part1_form, part2_form, google_results_view, answer_bonus_seconds")
    .eq("id", quizrunId)
    .maybeSingle();
  // Anon RLS returns no row here (expected) — log but don't throw.
  if (hostLinksError) {
    logger.error("Error fetching host-only quizrun links", serializeError(hostLinksError));
  }

  return transformQuizrunRow(
    { ...(quizrun as QuizrunRow), ...(hostLinks ?? {}) },
    quizrunEvents,
  );
};

/**
 * Set (or clear) an event's actual gun-time. Backed by the set_event_gun_time
 * RPC, which is admin/location-host scoped — hosts only have SELECT on `event`,
 * so this is their write path for gun time.
 *
 * @param eventId    Target event id.
 * @param gunTimeIso UTC ISO string, or null to clear ("not started").
 */
export const setEventGunTime = async (
  eventId: number,
  gunTimeIso: string | null,
): Promise<void> => {
  const { error } = await supabase.rpc("set_event_gun_time", {
    p_event_id: eventId,
    p_gun_time: gunTimeIso,
  });
  if (error) {
    throw error;
  }
};

export function listEventLocationIds() {
  return supabase.from("event").select("location_id");
}

export function createQuizrun(row: TablesInsert<"quizrun">) {
  return supabase
    .from("quizrun")
    .insert(row)
    .select("id")
    .single();
}

export function updateQuizrun(id: number, patch: TablesUpdate<"quizrun">) {
  return supabase
    .from("quizrun")
    .update(patch)
    .eq("id", id);
}

export function deleteEvents(ids: number[]) {
  return supabase.from("event").delete().in("id", ids);
}

export function updateEventRow(id: number, patch: TablesUpdate<"event">) {
  return supabase
    .from("event")
    .update(patch)
    .eq("id", id);
}

export function insertEvent(row: TablesInsert<"event">) {
  return supabase.from("event").insert(row);
}
